/*
 * Purpose: Fire persisted schedules while app is open (client-side runner)
 * Last modified: 2026-06-28 | Agent
 * Completeness: 88
 */
import { useEffect, useRef } from 'react';
import { toastContextualIssue } from '@/lib/contextualIssuePolicy';
import { toast } from 'sonner';
import { missionMapHref } from '@/lib/appRoutes';
import { assertCanRunFlows, assertCanUseFeature } from '@/lib/subscriptionEnforce';
import {
  isScheduleDue,
  loadSchedules,
  markScheduleRan,
  saveSchedules,
} from '@/lib/workflowSchedules';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useExecutionRuntimeStore } from '@/stores/executionRuntimeStore';
import { WorkflowValidator } from '@/services/validation';
import {
  openConnectorFixForWorkflow,
  openConnectorFixFromError,
} from '@/stores/connectorFixStore';

export function ScheduleRunner() {
  const ticking = useRef(false);

  useEffect(() => {
    const tick = async () => {
      if (ticking.current) return;
      ticking.current = true;
      try {
        if (!assertCanUseFeature('scheduled_flows')) return;

        const schedules = loadSchedules();
        const due = schedules.filter((s) => isScheduleDue(s));
        if (due.length === 0) return;

        const { workflows, executeWorkflow } = useWorkflowStore.getState();
        const runtime = useExecutionRuntimeStore.getState();
        let updated = [...schedules];

        for (const schedule of due) {
          const workflow = workflows.find((w) => w.id === schedule.workflowId);
          if (!workflow) continue;
          if (runtime.runningIds().includes(workflow.id)) continue;
          if (!assertCanRunFlows()) continue;

          const errors = WorkflowValidator.validate(workflow).filter((e) => e.type === 'error');
          if (errors.length > 0) {
            toast.error(`Schedule skipped — fix "${workflow.name}" validation first`, {
              action: { label: 'Open flows', onClick: () => { window.location.href = '/flows'; } },
            });
            updated = updated.map((s) =>
              s.id === schedule.id ? markScheduleRan(s) : s
            );
            continue;
          }

          if (
            openConnectorFixForWorkflow(workflow, {
              onRetry: () => void tick(),
            })
          ) {
            updated = updated.map((s) =>
              s.id === schedule.id ? markScheduleRan(s) : s
            );
            continue;
          }

          const controller = new AbortController();
          runtime.registerRun(workflow.id, workflow.name, controller);
          toast.message(`Scheduled run: ${workflow.name}`);
          try {
            await executeWorkflow(controller.signal);
            if (!controller.signal.aborted) {
              toast.success(`Scheduled run completed: ${workflow.name}`);
            }
          } catch (error) {
            if (
              !openConnectorFixFromError(error, {
                workflow,
                onRetry: () => void tick(),
              })
            ) {
              toastContextualIssue(`Scheduled run failed: ${workflow.name}`, {
                label: 'Open map',
                href: missionMapHref(workflow.id),
              });
            }
          } finally {
            runtime.unregisterRun(workflow.id);
          }

          updated = updated.map((s) => (s.id === schedule.id ? markScheduleRan(s) : s));
        }

        saveSchedules(updated);
      } finally {
        ticking.current = false;
      }
    };

    const id = window.setInterval(() => void tick(), 30_000);
    void tick();
    return () => window.clearInterval(id);
  }, []);

  return null;
}
